import { skipToken } from '@reduxjs/toolkit/query';
import useSearchHighlight from '@rio-cloud/rio-uikit/hooks/useSearchHighlight';
import Notification from '@rio-cloud/rio-uikit/Notification';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import TableViewToggles, { type TableViewTogglesViewType } from '@rio-cloud/rio-uikit/TableViewToggles';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router';

import { useGetFleetGroupsQuery } from '#api/tagsApi.slice.ts';
import { useGetTourDocumentsQuery } from '#api-bridge/documentApi.ts';
import { useGetOrdersQuery } from '#api-bridge/orderApi.ts';
import { useResolveProblemMutation } from '#api-bridge/problemApi.ts';
import { useResources } from '#api-bridge/resourcesApi.ts';
import { useGetTour, useGetToursQuery } from '#api-bridge/tourApi.ts';
import { useApplicationLayoutBottomBar } from '#components/applayout/ApplicationLayoutBottomBarContext.tsx';
import { ServiceInMaintenance } from '#components/error/ServiceInMaintenance.tsx';
import { MissingPocketDriverDataSourceBanner } from '#components/resources/MissingPocketDriverDataSourceBanner.tsx';
import { MissingPocketDriverDataSourceBannerStateSync } from '#components/resources/MissingPocketDriverDataSourceBannerState.tsx';
import ErrorFallback from '#components/shared/ErrorFallback.tsx';
import { useDatePeriodFilter } from '#components/shared/useDatePeriodFilter.ts';
import { CreateTourDialog } from '#components/tour/CreateTourDialog/CreateTourDialog.tsx';
import { TourDetailsSidebar } from '#components/tour/sidebar/TourDetailsSidebar.tsx';
import { getTourTableColumns, TourTable, type TourTableColumnConfig } from '#components/tour/TourTable.tsx';
import { TourTableToolbar } from '#components/tour/TourTableToolbar.tsx';
import { useFeatureToggle } from '#configuration/featureFlags/useFeatureToggle.ts';
import { hasMissingPocketDriverDataSource } from '#domain/VehicleWarning.ts';
import { useDebouncedSearch } from '#hooks/shared/useDebouncedSearch.ts';
import { usePersistedTableColumns } from '#hooks/shared/usePersistedTableColumns.ts';
import { parseAsArrayOf, stringParser, useSearchParam } from '#hooks/shared/useSearchParam.ts';
import { useTableSorting } from '#hooks/shared/useTableSorting.ts';
import { useFilteredTours } from '#hooks/tour/useFilteredTours.ts';
import { useAppNavigate } from '#routes/AppNavLink.tsx';
import { routes } from '#routes/routes.ts';
import { isHttpUnavailableStatusCodeError } from '#utils/errorCheck.ts';
import { addSortableTourKey, getTourVehicle } from '#utils/tours.util.ts';
import { handleNotificationForUpdateData } from '#utils/util.ts';

const TOUR_SORTABLE_COLUMN_KEYS = [
    'searchName',
    'fleetGroupName',
    'licensePlate',
    'firstStopEndTarget',
    'lastStopEndTarget',
    'progressPercentage',
] as const;

export const deleteTourFilterSearchParams = (searchParams: URLSearchParams) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('status');
    nextSearchParams.delete('groupIds');
    nextSearchParams.delete('period');
    nextSearchParams.delete('from');
    nextSearchParams.delete('to');
    return nextSearchParams;
};

export const TourPage = () => {
    const navigate = useAppNavigate();
    const tableRef = useRef<HTMLDivElement | null>(null);
    const intl = useIntl();
    const showServiceOrders = useFeatureToggle('showServiceOrders', false);

    const { search, searchInput, onSearch } = useDebouncedSearch();
    const dateFilter = useDatePeriodFilter();
    const [statusFilter, setStatusFilter] = useSearchParam('status', parseAsArrayOf(stringParser).default([]));
    const [groupFilter, setGroupFilter] = useSearchParam('groupIds', parseAsArrayOf(stringParser).default([]));
    const [, setSearchParams] = useSearchParams();
    const [tableViewType, setTableViewType] = useState<TableViewTogglesViewType>(TableViewToggles.VIEW_TYPE_TABLE);
    const defaultTableColumns = useMemo(() => getTourTableColumns(intl), [intl.locale]);
    const {
        columns: tableColumns,
        handleColumnOrderChange,
        handleColumnResize,
        handleColumnVisibilityChange,
        resetColumns: resetTableColumns,
        resetVersion: tableResetVersion,
    } = usePersistedTableColumns<TourTableColumnConfig>({
        defaultColumns: defaultTableColumns,
        storageKeyPrefix: 'order-communication.tours.table',
    });

    const { vehicles, refetch: refetchResources } = useResources();
    const {
        data: fleetGroups = [],
        isFetching: isFetchingFleetGroups,
        isError: isFleetGroupsError,
    } = useGetFleetGroupsQuery(undefined);
    const { tourId: selectedTourId } = useParams();
    const [isDocumentDialogOpen, setIsDocumentDialogOpen] = useState<boolean>(false);
    const [showCreateTourDialog, setShowCreateTourDialog] = useState(false);

    const {
        isLoading,
        data = [],
        error,
        refetch: refetchTours,
        isFetching: isFetchingTours,
    } = useGetToursQuery(
        { groupIds: groupFilter },
        {
            pollingInterval: 30000,
            skipPollingIfUnfocused: true,
            skip: isDocumentDialogOpen,
        }
    );

    const dataWithSortableKey = addSortableTourKey(data, vehicles, fleetGroups);
    const {
        sortedItems: sortedTours,
        sortKey,
        sortDirection,
        handleSortByColumn,
    } = useTableSorting(dataWithSortableKey, 'statusWeight', 'lastStopEndTarget', TOUR_SORTABLE_COLUMN_KEYS);

    const { filteredTours, activeTour } = useFilteredTours({
        tours: sortedTours,
        vehicles,
        search,
        statusFilter,
        range: dateFilter.range,
        selectedTourId,
    });

    const { data: selectedTourDetails, refetch: refetchTour } = useGetTour(selectedTourId ?? skipToken, {
        pollingInterval: 30000,
        skipPollingIfUnfocused: true,
        skip: isDocumentDialogOpen,
    });
    const { data: orders = [] } = useGetOrdersQuery(
        {
            pollingInterval: 30000,
            skipPollingIfUnfocused: true,
            skip: isDocumentDialogOpen,
        },
        showServiceOrders ? ['transport', 'service'] : ['transport']
    );
    const [resolveProblem] = useResolveProblemMutation();

    const { data: tourDocuments } = useGetTourDocumentsQuery(
        // biome-ignore lint/style/noNonNullAssertion: checked in skip option
        { tourId: selectedTourId! },
        {
            pollingInterval: 30000,
            skipPollingIfUnfocused: true,
            skip: isDocumentDialogOpen || !selectedTourId,
        }
    );

    useSearchHighlight({
        ref: tableRef,
        query: searchInput,
        caseSensitive: false,
        deps: [filteredTours],
    });

    const handleTourRefetch = useCallback(() => {
        refetchTours().then(({ isSuccess }) => {
            if (isSuccess) {
                handleNotificationForUpdateData(intl);
            }
        });
        refetchResources();
        refetchTour();
    }, [intl.locale, refetchResources, refetchTour, refetchTours]);

    const onFilterStatus = (input: string[]) => setStatusFilter(input);
    const onFilterGroups = (input: string[]) => setGroupFilter(input);
    const refreshBottomBar = useMemo(
        () => ({
            ariaLabel: intl.formatMessage({
                id: 'intl-msg:ordercom-cloud-frontend.tours.refresh.action',
                defaultMessage: 'Refresh tours',
            }),
            isRefreshing: isFetchingTours,
            onRefresh: handleTourRefetch,
            testId: 'refresh-button',
            trackingKey: 'intl-msg:ordercom-cloud-frontend.tours.refresh.action',
        }),
        [handleTourRefetch, intl.locale, isFetchingTours]
    );
    const resetFilters = () => {
        setSearchParams(deleteTourFilterSearchParams);
    };

    useApplicationLayoutBottomBar(isLoading || error ? null : refreshBottomBar);

    if (isHttpUnavailableStatusCodeError(error)) {
        return <ServiceInMaintenance />;
    }

    if (error) {
        return <ErrorFallback onRetry={refetchTours} />;
    }

    if (isLoading) {
        return <Spinner />;
    }

    const hasNoTours =
        dataWithSortableKey.length === 0 &&
        statusFilter.length === 0 &&
        groupFilter.length === 0 &&
        !dateFilter.hasActiveFilter &&
        search === '';
    const openOrders = orders.filter(order => order.status === 'open');
    const selectedTourVehicle = selectedTourDetails ? getTourVehicle(selectedTourDetails, vehicles) : undefined;
    const selectedTourVehicleName = selectedTourVehicle?.plate?.trim() || selectedTourVehicle?.searchName;
    const missingPocketDriverDataSourceCount = vehicles.filter(hasMissingPocketDriverDataSource).length;

    const handleSolveProblem = async (tourId: string) => {
        try {
            await resolveProblem(tourId).unwrap();
            Notification.success(
                intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.tours.problem.single.success',
                    defaultMessage: 'Problem successfully resolved',
                })
            );
        } catch (error) {
            console.error('Failed to solve tour problem: ', error);
            Notification.error(
                intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.tours.problem.single.failure',
                    defaultMessage: 'Problem could not be resolved',
                })
            );
        }
    };

    const selectTour = (tourId: string | undefined) =>
        navigate(tourId ? `${routes.TOURS}/${tourId}` : `${routes.TOURS}`);
    const openCreateTourDialog = () => setShowCreateTourDialog(true);
    const closeCreateTourDialog = () => setShowCreateTourDialog(false);

    return (
        <>
            <MissingPocketDriverDataSourceBannerStateSync missingCount={missingPocketDriverDataSourceCount} />
            <MissingPocketDriverDataSourceBanner vehicles={vehicles} />
            <TourTableToolbar
                search={searchInput}
                onSearch={onSearch}
                statusFilter={statusFilter}
                onFilterStatus={onFilterStatus}
                groupFilter={groupFilter}
                onFilterGroups={onFilterGroups}
                fleetGroups={fleetGroups}
                isFleetGroupsLoading={isFetchingFleetGroups}
                isFleetGroupsDisabled={isFleetGroupsError}
                onResetFilters={resetFilters}
                dateFilter={dateFilter}
                onOpenCreateTourDialog={openCreateTourDialog}
                columns={tableColumns}
                onColumnOrderChange={handleColumnOrderChange}
                onColumnVisibilityChange={handleColumnVisibilityChange}
                onResetColumns={resetTableColumns}
                onViewTypeChange={setTableViewType}
                viewType={tableViewType}
            />
            <CreateTourDialog
                isOpen={showCreateTourDialog}
                close={closeCreateTourDialog}
                orders={[]}
                availableOrders={openOrders}
            />
            {selectedTourId && selectedTourDetails && (
                <TourDetailsSidebar
                    setSelectedTourId={selectTour}
                    setDocumentDialogState={setIsDocumentDialogOpen}
                    selectedTour={selectedTourDetails}
                    handleSolveProblem={handleSolveProblem}
                    tourDocuments={tourDocuments?.items}
                    vehicleAssetId={selectedTourDetails.truck.id}
                    vehicleName={selectedTourVehicleName}
                />
            )}
            <TourTable
                key={tableResetVersion}
                tableRef={tableRef}
                activeTourId={activeTour?.id}
                hasNoTours={hasNoTours}
                tours={filteredTours}
                vehicles={vehicles}
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSortChange={handleSortByColumn}
                onColumnOrderChange={handleColumnOrderChange}
                onColumnResize={handleColumnResize}
                onRowClick={selectTour}
                columns={tableColumns}
                onViewTypeChange={setTableViewType}
                viewType={tableViewType}
            />
        </>
    );
};
